/**
 * MarketBadge — Iter-191.1
 *
 * Compact pill that surfaces a talent's market affiliation in lists,
 * invite previews, and admin summary surfaces.
 *
 * Priority (highest first):
 *   1. Primary market match  → emerald / teal pill ("{Metro} Market")
 *   2. Secondary market      → lighter neutral pill
 *   3. (no markets)          → null (caller may render a radius distance badge instead)
 *
 * Optional "highlightMarketIds" prop lets the parent pass a set of currently-targeted
 * market UUIDs (e.g. from the active TalentDiscovery filter, an open broadcast composer,
 * or a shift's market_id). When provided, the badge color escalates to a stronger
 * emerald to call out that the talent is in the targeted region.
 *
 * Props:
 *   markets             talent's full markets[] (each {id, slug, metro_name, is_primary})
 *   primaryMarket       optional shortcut object (overrides scanning markets[])
 *   highlightMarketIds  optional Set/array of UUIDs that the caller is targeting
 *   size                'xs' (default) | 'sm'
 *   variant             'auto' (default — picks color based on priority) | 'neutral'
 *   testId              optional, defaults to 'market-badge'
 */
import React from 'react';
import { MapPin, Star } from 'lucide-react';

function _shortName(metroName) {
  // "Atlanta, GA" → "Atlanta Market"
  // "Washington, DC-MD-VA" → "Washington Market"
  // "New York, NY-NJ-CT" → "New York Market"
  // "Tampa Bay / Sarasota, FL" → "Tampa Bay Market"
  if (!metroName) return 'Market';
  const beforeComma = metroName.split(',')[0].trim();
  const beforeSlash = beforeComma.split('/')[0].trim();
  return `${beforeSlash} Market`;
}

export default function MarketBadge({
  markets = [],
  primaryMarket = null,
  highlightMarketIds = null,
  size = 'xs',
  variant = 'auto',
  testId = 'market-badge',
}) {
  const list = Array.isArray(markets) ? markets : [];
  const primary = primaryMarket || list.find((m) => m && m.is_primary) || null;

  // Nothing to render
  if (!primary && list.length === 0) return null;

  // Highlight set
  const hlSet = (() => {
    if (!highlightMarketIds) return null;
    if (highlightMarketIds instanceof Set) return highlightMarketIds;
    return new Set(highlightMarketIds);
  })();

  // Resolve the most relevant market to display
  let chosen = primary;
  let matchType = primary ? 'primary' : null;
  let isHighlighted = false;

  if (hlSet && hlSet.size > 0) {
    if (primary && hlSet.has(primary.id)) {
      chosen = primary;
      matchType = 'primary';
      isHighlighted = true;
    } else {
      const secMatch = list.find((m) => !m.is_primary && hlSet.has(m.id));
      if (secMatch) {
        chosen = secMatch;
        matchType = 'secondary';
        isHighlighted = true;
      }
    }
  }
  // No highlight — fall back to primary; secondary only if no primary
  if (!chosen && list.length > 0) {
    chosen = list[0];
    matchType = 'secondary';
  }
  if (!chosen) return null;

  const padding = size === 'sm' ? 'px-2.5 py-1' : 'px-2 py-0.5';
  const text = size === 'sm' ? 'text-[12px]' : 'text-[10px]';
  const icon = size === 'sm' ? 'w-3.5 h-3.5' : 'w-3 h-3';

  // Color logic
  let cls = 'bg-gray-100 text-gray-700 border-gray-200';
  if (variant !== 'neutral') {
    if (matchType === 'primary') {
      cls = isHighlighted
        ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
        : 'bg-emerald-50 text-emerald-800 border-emerald-200';
    } else if (matchType === 'secondary') {
      cls = isHighlighted
        ? 'bg-teal-100 text-teal-800 border-teal-300'
        : 'bg-slate-100 text-slate-700 border-slate-200';
    }
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium leading-none ${padding} ${text} ${cls}`}
      title={chosen.metro_name}
      data-testid={testId}
      data-market-id={chosen.id}
      data-market-match={matchType}
    >
      {matchType === 'primary' ? (
        <Star className={`${icon} ${isHighlighted ? 'fill-white text-white' : 'fill-emerald-500 text-emerald-500'}`} />
      ) : (
        <MapPin className={icon} />
      )}
      <span>{_shortName(chosen.metro_name)}</span>
      {list.length > 1 && (
        <span className={`opacity-70 ${size === 'sm' ? 'text-[10px]' : 'text-[9px]'}`}>
          +{list.length - 1}
        </span>
      )}
    </span>
  );
}
